import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Link as RouterLink, matchPath, useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Drawer,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
  alpha,
  Stack,
  Typography,
  useMediaQuery,
  IconButton,
  LinearProgress,
  linearProgressClasses,
  Collapse,
  Divider
} from '@mui/material'
import { items, OnboardingHub } from '@layouts/dashboard/config'
import { CloseOutlined, ExpandLess, ExpandMore } from '@mui/icons-material'
import { Logo } from '@components/logo'
import SideNavFooter from '@layouts/dashboard/side-nav-footer'
import { useDispatch, useSelector } from 'react-redux'
import { LogoSymbol } from '@components/logoSymbol'
import { useAuth } from '@auth/auth'
import { setActualTotalCount, useGetCampaignEmailsMutation } from 'src/services/unibox-service'
import UpgradeToPremiumButton from './UpgradeToPremiumBtn'
import useUserVerification from 'src/hooks/useUserVerification'
import { styled } from '@mui/material/styles'
import WorkspaceSelector from 'src/components/WorkspaceSelector'
import { motion } from 'framer-motion'
import { useUser, useWorkspace } from 'src/hooks/user'
import { useOnboarding } from './layout'
import { ManageSubUsersStatus } from 'src/config'
import { setWhiteLabelTeamMemberStatus } from 'src/store/reducers/whiteLabelTeamMemberStatus'
import { useGetQuery } from 'src/services/api/network-request-service'
import { WHITE_LABEL_TEAM, WHITE_LABEL_TEAM_QUERY_KEYS } from 'src/services/api/api-constant'
import LockIcon from '@mui/icons-material/Lock'
import { useGetCurrentPlanQuery } from 'src/services/billing-service.js'

const SIDE_NAV_WIDTH = 264
const scrollBarStyle = {
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px'
  },
  '&::-webkit-scrollbar-track': {
    borderRadius: '60px'
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '10px',
    border: '3px solid rgba(0, 0, 0, 0)',
    backgroundClip: 'padding-box'
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.5)'
  }
}

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 5,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: '#FFFFFF4D',
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800]
    })
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.primary.contrastText,
    ...theme.applyStyles('dark', {
      backgroundColor: '#308fe8'
    })
  }
}))

const openedMixin = (theme) => ({
  width: SIDE_NAV_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden'
})

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(8)} + 1px)`
})

export const SideNav = ({ mobileMenuOpen, setMobileMenuOpen, navMenuOpen }) => {
  const [open, setOpen] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('lg'))
  const theme = useTheme()
  const { canEdit } = useAuth()
  const workspace = useWorkspace()
  const user = useUser()
  const { isFreeUser, isWhiteLabelUser, isExcludedIntegrationFeature, isEarlyBirdAccessUser, isAppSumoUser, hasAnyLeadPlan, hasAnyOutreachPlan } = useUserVerification()
  const { completionPercentage } = useOnboarding()
  const [getCampaignEmails] = useGetCampaignEmailsMutation()
  const actualTotalCount = useSelector(state => state.inboxhub.actualTotalCount)
  const { partnerStatus, permissions } = useSelector(state => state.whiteLabelTeamMemberStatus)
  const { data: userPermission } = useGetQuery(
    WHITE_LABEL_TEAM_QUERY_KEYS.GET_USER_PERMISSION,
    WHITE_LABEL_TEAM.GET_USER_PERMISSION
  )
  const { data: fetchedCurrentPlan } = useGetCurrentPlanQuery()
  const [currentPlan, setCurrentPlan] = useState(fetchedCurrentPlan)

  const actualTotalCountValue = actualTotalCount
  const actualTotalCountApi = totalCount

  const handleClick = (event, item) => {
    if (item.subItems) {
      event.preventDefault()
      setOpen(!open)
    } else {
      setMobileMenuOpen(false)
    }
  }
  const isFeatureLocked = useCallback((featureId) => {
    if (!currentPlan) return false

    const currentLeadPlanId = currentPlan?.subscription?.leads?.priceId || currentPlan?.subscription?.leads?.planId
    const currentOutreachPlanId = currentPlan?.subscription?.sendingWarmup?.priceId || currentPlan?.subscription?.sendingWarmup?.planId

      if (isEarlyBirdAccessUser || isAppSumoUser) {
        return false
      }

      if (!hasAnyLeadPlan && !hasAnyOutreachPlan) {
        return false
      }

      if (['Lead Finder', 'Lists'].includes(featureId)) {
        return hasAnyOutreachPlan && !hasAnyLeadPlan
      }

      if (['Accounts', 'Campaigns', 'Analytics', 'New_Analytics', 'InboxHub', 'dealPipeline'].includes(featureId)) {
        return hasAnyLeadPlan && !hasAnyOutreachPlan
      }

    if (!hasSpecificLeadPlan && !hasSpecificOutreachPlan) {
      return false
    }},
    [currentPlan, isEarlyBirdAccessUser, isAppSumoUser, hasAnyLeadPlan, hasAnyOutreachPlan]
  )

  const handleLockedFeatureClick = (featureId) => {
    // Open upgrade modal with appropriate plan type
    if (['Lead Finder', 'Lists'].includes(featureId)) {
      navigate('/settingsUpdated/signup/billing?type=LEADS')
    } else {
      navigate('/settingsUpdated/signup/billing?type=OUTREACH')
    }
  }

  const organizeNavItems = () => {
    const filteredItems = items.filter((item) => {
      const excludedFeatures = isExcludedIntegrationFeature ? ['Support Center', 'Integrations'] : ['Support Center']
      if (isWhiteLabelUser && excludedFeatures.includes(item.id)) {
        return false
      }

      if (item.id === 'Partner/White Label') {
        if (user?.isPartner) {
          return true
        }

        if (partnerStatus === 'teamMember') {
          if (!permissions) {
            return false
          }

          const filteredSubItems = []

          for (const subItem of item.subItems) {
            let shouldInclude = false

            switch (subItem.id) {
              case 'Manage Users':
                shouldInclude = permissions.manageSubUsers === ManageSubUsersStatus.ALL ||
                  permissions.manageSubUsers === ManageSubUsersStatus.ASSIGNED
                break
              case 'Branding & Configuration':
                shouldInclude = Boolean(permissions.manageBranding)
                break
              case 'plans':
                shouldInclude = Boolean(permissions.managePlans)
                break
              case 'Team Management':
                shouldInclude = Boolean(permissions.manageTeamMembers)
                break
              default:
                shouldInclude = false
            }

            if (shouldInclude) {
              filteredSubItems.push(subItem)
            }
          }

          item.subItems = filteredSubItems
          return filteredSubItems.length > 0
        }
        return false
      }

      return true
    })

    const databaseItems = filteredItems.filter(item => ['Lead Finder', 'Lists'].includes(item.id))
    const partnerItems = filteredItems.filter(item => ['Partner/White Label'].includes(item.id))
    const outreachItems = filteredItems.filter(item => !['Lead Finder', 'Lists', 'Partner/White Label'].includes(item.id))

    return {
      databaseItems,
      partnerItems,
      outreachItems
    }
  }

  const { databaseItems, partnerItems, outreachItems } = useMemo(() => organizeNavItems(),
    [items, isWhiteLabelUser, user?.isPartner, partnerStatus, permissions, isExcludedIntegrationFeature])

  useEffect(() => {
    if (user?.firstLogin && !user?.emailVerified) {
      navigate('/loginProtection')
    }
  }, [user?.firstLogin, user?.emailVerified])

  useEffect(() => {
    if (!workspace?._id) {
      return
    }
    const getUnreadCount = async () => {
      const docs = await getCampaignEmails({
        unread: true,
        workspaceId: workspace._id
      })
      setTotalCount(docs?.data?.totalEmailCount)
      dispatch(setActualTotalCount(docs?.data?.totalEmailCount))
    }

    getUnreadCount()
  }, [workspace?._id])

  useEffect(() => {
    if (!userPermission) {
      return
    }

    const statusData = {
      partnerStatus: 'teamMember',
      permissions: {
        manageSubUsers:
          userPermission?.member?.permissions?.manageSubUsers ||
          ManageSubUsersStatus.NONE,
        manageBranding: userPermission?.member?.permissions?.manageBranding || false,
        managePlans: userPermission?.member?.permissions?.managePlans || false,
        manageTeamMembers: userPermission?.member?.permissions?.manageTeamMembers || false
      }
    }

    dispatch(setWhiteLabelTeamMemberStatus(statusData))
  }, [userPermission, dispatch])

  useEffect(() => {
    setCurrentPlan(fetchedCurrentPlan)
  }, [fetchedCurrentPlan])

  const renderNavSection = (sectionTitle, sectionItems) => {
    if (sectionItems.length === 0) return null

    return (
      <Stack spacing={0} sx={{ px: navMenuOpen || isMobile ? 1 : 0 }}>
        <Typography
          sx={{
            color: alpha(theme.palette.primary.contrastText, 0.5),
            fontWeight: '700',
            fontSize: '12px',
            mb: 1
          }}
        >
          {navMenuOpen || isMobile ? sectionTitle : ''}
        </Typography>
        <List sx={{ width: '100%', m: 0, p: 0, px: navMenuOpen || isMobile ? 0 : '4px' }}>
          {sectionItems.map((item, i) => {
            const active = matchPath({ path: item.href, end: true }, location.pathname)
            const isLocked = isFeatureLocked(item.id)

            if (item?.label === 'Lead Finder' && !canEdit) {
              return null
            }
            return (
              <React.Fragment key={i}>
                <Tooltip
                  title={!navMenuOpen && item.label}
                  placement='right-start'
                  arrow
                >
                  <ListItem
                    id={item.id}
                    disablePadding
                    component={isLocked ? 'div' : RouterLink}
                    key={i}
                    to={!isLocked ? item.href : undefined}
                    onClick={(e) => {
                      if (isLocked) {
                        e.preventDefault()
                        handleLockedFeatureClick(item.id)
                      } else {
                        handleClick(e, item)
                      }
                    }}
                    sx={{
                      ml: `${item.subItems && !navMenuOpen && '-8px'}`,
                      pb: { xs: '10px', md: '0.5rem' },
                      zIndex: 999,
                      cursor: isLocked ? 'pointer' : 'default'
                    }}
                  >
                    <Tooltip title='' placement='right' arrow>
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            backgroundColor: active && alpha(theme.palette.background.paper, 0.2),
                            borderRadius: '10px',
                            px: 1,
                            minWidth: '40px',
                            width: '100%',
                            '&:hover': {
                              backgroundColor:
                                !active && alpha(theme.palette.background.paper, 0.05)
                            }
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 'auto',
                              color: theme.palette.primary.contrastText,
                              py: '8px'
                            }}
                          >
                            {item.icon(theme.palette.primary.contrastText)}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{
                              variant: 'caption',
                              sx: {
                                color: theme.palette.primary.contrastText,
                                fontSize: '14px',
                                fontWeight: 700,
                                lineHeight: '18px',
                                letterSpacing: '0px',
                                textAlign: 'left',
                                ml: 1
                              }
                            }}
                            sx={{ ml: 1, display: navMenuOpen || isMobile ? 'block' : 'none' }}
                          />
                          {isLocked && (
                            <LockIcon
                              sx={{
                                color: '#fff',
                                fontSize: navMenuOpen || isMobile ? '18px' : '14px',
                                ml: navMenuOpen || isMobile ? 1 : '0px'
                              }}
                            />
                          )}
                          {(isMobile || navMenuOpen) &&
                            isFreeUser &&
                            item.id === 'Integrations' && (
                              <LockIcon sx={{ color: '#fff', fontSize: '18px' }} />
                            )}
                          {item.label === 'InboxHub' &&
                            actualTotalCount !== 0 &&
                            actualTotalCountApi !== 0 &&
                            ((workspace?.analytics?.uiForUnibox && workspace?.enterprise) ||
                              !workspace?.enterprise) && (
                              <Box
                                sx={{
                                  minWidth: '1.6em',
                                  height: '1.6em',
                                  borderRadius: '0.8em',
                                  border: '0.05em solid white',
                                  backgroundColor: theme.palette.primary.contrastText,
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  fontSize: '0.8em',
                                  fontWeight: '700',
                                  color: theme.palette.primary.main,
                                  padding: '5px'
                                }}
                              >
                                {actualTotalCountValue == null
                                  ? actualTotalCountApi
                                  : actualTotalCountValue}
                              </Box>
                            )}
                          {item.subItems && (!navMenuOpen || navMenuOpen || isMobile) && (
                            <ListItemIcon sx={{
                              minWidth: 'auto',
                              color: theme.palette.primary.contrastText,
                              py: '8px'
                            }}
                            >
                              {open
                                ? (
                                  <ExpandLess
                                    sx={{ color: theme.palette.primary.contrastText }}
                                  />
                                )
                                : (
                                  <ExpandMore
                                    sx={{ color: theme.palette.primary.contrastText }}
                                  />
                                )}
                            </ListItemIcon>
                          )}
                        </Box>
                      </>
                    </Tooltip>
                  </ListItem>
                </Tooltip>
                {item.subItems && (
                  <Collapse in={open} timeout='auto' unmountOnExit>
                    <List disablePadding>
                      {item.subItems.map((subItem) => (
                        <ListItem
                          id={subItem.id}
                          disablePadding
                          component={RouterLink}
                          key={subItem.href}
                          to={subItem.href}
                          onClick={(e) => handleClick(e, subItem)}
                          style={{
                            paddingLeft: navMenuOpen || isMobile ? '10px' : '0px'
                          }}
                          sx={{
                            px: `${navMenuOpen && '24px'}`,
                            zIndex: 999
                          }}
                        >
                          <Tooltip title={!navMenuOpen && subItem.label} placement='right' arrow>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                backgroundColor:
                                  active && alpha(theme.palette.background.paper, 0.2),
                                borderRadius: '10px',
                                minWidth: '40px',
                                width: '100%',
                                '&:hover': {
                                  backgroundColor:
                                    (!active || !navMenuOpen) && alpha(theme.palette.background.paper, 0.05)
                                }
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 'auto',
                                  color: theme.palette.primary.contrastText,
                                  py: '8px',
                                  pl: '8px'
                                }}
                              >
                                {subItem.icon(theme.palette.primary.contrastText)}
                              </ListItemIcon>
                              <ListItemText
                                primary={subItem.label}
                                primaryTypographyProps={{
                                  variant: 'caption',
                                  sx: {
                                    color: theme.palette.primary.contrastText,
                                    fontSize: '14px',
                                    fontWeight: 700,
                                    lineHeight: '18px',
                                    letterSpacing: '0px',
                                    textAlign: 'left',
                                    ml: 1
                                  }
                                }}
                                sx={{ ml: 1, display: navMenuOpen || isMobile ? 'block' : 'none' }}
                              />
                            </Box>
                          </Tooltip>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            )
          })}
        </List>
      </Stack>
    )
  }

  return (
    <Drawer
      open={isMobile ? mobileMenuOpen : navMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      variant={isMobile ? 'temporary' : 'permanent'}
      sx={{
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        width: SIDE_NAV_WIDTH,
        flexShrink: 0,
        ...(navMenuOpen &&
          !isMobile && {
          ...openedMixin(theme),
          '& .MuiDrawer-paper': openedMixin(theme)
        }),
        ...(!navMenuOpen &&
          !isMobile && {
          ...closedMixin(theme),
          '& .MuiDrawer-paper': closedMixin(theme)
        })
      }}
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.primary.main,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          zIndex: (theme) => theme.zIndex.appBar - 100,
          border: 'none'
        }
      }}
    >
      {isMobile && (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            onClick={() => setMobileMenuOpen(false)}
            sx={{ color: theme.palette.primary.contrastText }}
          >
            <CloseOutlined />
          </IconButton>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
          px: 1,
          py: { xs: 0, md: 3 },
          overflowY: { xs: 'auto', lg: 'hidden' },
          ':hover': {
            overflowY: 'auto'
          },
          overflowX: 'hidden',
          ...scrollBarStyle
        }}
      >
        <Stack spacing={5}>
          <Box
            component={RouterLink}
            to='/'
            sx={{
              display: 'flex',
              justifyContent: !theme.themeLogo ? 'center' : 'start',
              alignItems: !theme.themeLogo ? 'center' : 'normal',
              width: '150px',
              height: '30px',
              px: 1,
              textDecoration: 'none',
              ...(!theme.themeLogo && {
                maxWidth: navMenuOpen || isMobile ? '150px' : '45px',
                maxHeight: '40px',
                overflow: 'hidden',
                transition: theme.transitions.create(['max-width'], {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.leavingScreen
                })
              })
            }}
          >

            {navMenuOpen || isMobile
              ? (
                !theme.themeLogo
                  ? (
                    <Logo
                      color={theme.palette.primary.contrastText}
                      logoTextColor={theme.palette.primary.contrastText}
                    />
                  )
                  : (
                    <>
                      <img src={theme.themeLogo} alt='company' width={100} height={50} />
                    </>
                  )
              )
              : !theme.themeLogo
                ? (
                  <LogoSymbol color={theme.palette.primary.contrastText} />
                )
                : (
                  <Avatar alt='company' src={theme.themeLogo} className='!mx-0' />
                )}
          </Box>
          <Stack spacing={2} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {renderNavSection('LEADS', databaseItems)}
            {(!navMenuOpen && !isMobile) &&
              (<Divider sx={{ width: '100%' }} />)}
            {renderNavSection('OUTREACH', outreachItems)}
            {(!navMenuOpen && !isMobile && isWhiteLabelUser) &&
              (<Divider sx={{ width: '100%' }} />)}
            {renderNavSection('PARTNER', partnerItems)}
          </Stack>
        </Stack>
        <Stack sx={{ display: 'flex', gap: '20px', mt: 3 }}>
          <Stack direction='column' spacing={1} sx={{ px: navMenuOpen || isMobile ? 1 : 0 }}>
            <Tooltip title='Onboarding Hub' placement='right' arrow>
              <Box
                component={RouterLink}
                to='/onboardinghub'
                sx={{ textDecoration: 'none', cursor: 'pointer' }}
              >
                <Box
                  component={motion.div}
                  sx={{
                    p: 1,
                    borderRadius: '10px',
                    minWidth: '40px',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.background.paper, 0.1)
                    }
                  }}
                  animate={{
                    boxShadow: [
                      '0px 0px 10px rgba(89, 222, 255, 0.72)',
                      '0px 0px 30px rgba(0, 153, 255, 0.8)',
                      '0px 0px 60px rgba(33, 150, 243, 0.6)',
                      '0px 0px 30px rgba(0, 153, 255, 0.8)',
                      '0px 0px 10px rgba(89, 222, 255, 0.72)'
                    ]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }}
                >
                  <Stack
                    direction='row'
                    spacing={1}
                    sx={{
                      display: 'flex',
                      justifyContent: navMenuOpen || isMobile ? 'left' : 'center'
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: '0',
                        color: theme.palette.primary.contrastText,
                        py: '0px'
                      }}
                    >
                      {OnboardingHub.icon(true, theme.palette.primary.contrastText, 24)}
                    </ListItemIcon>
                    <Typography
                      sx={{
                        color: alpha(theme.palette.primary.contrastText, 1),
                        fontWeight: '700',
                        fontSize: '16px',
                        display: navMenuOpen || isMobile ? 'block' : 'none'
                      }}
                    >
                      {OnboardingHub.label}
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{
                      color: theme.palette.primary.contrastText,
                      fontWeight: '300',
                      fontSize: '14px',
                      marginY: '8px',
                      display: navMenuOpen || isMobile ? 'block' : 'none'
                    }}
                  >
                    {completionPercentage}% complete
                  </Typography>
                  <BorderLinearProgress
                    variant='determinate'
                    value={completionPercentage}
                    sx={{ display: navMenuOpen || isMobile ? 'block' : 'none' }}
                  />
                </Box>
              </Box>
            </Tooltip>
            {((isFreeUser || (!hasAnyLeadPlan || !hasAnyOutreachPlan)) && !isWhiteLabelUser && !isEarlyBirdAccessUser) && (
              <Stack
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%'
                }}
              >
                <UpgradeToPremiumButton navMenuOpen={navMenuOpen} isMobile={isMobile} />
              </Stack>
            )
            }
          </Stack>
          {(!navMenuOpen && !isMobile) &&
            <Divider sx={{ width: '100%', mt: 2 }} />}
          <Stack spacing={2} sx={{ px: navMenuOpen || isMobile ? 1 : 0 }}>
            <Typography
              sx={{
                color: alpha(theme.palette.primary.contrastText, 0.5),
                fontWeight: '700',
                fontSize: '12px'
              }}
            >
              {navMenuOpen || isMobile ? 'PROFILE' : ''}
            </Typography>
            <WorkspaceSelector isFreeUser={isFreeUser} navMenuOpen={navMenuOpen} isMobile={isMobile} />
            <SideNavFooter navMenuOpen={navMenuOpen} isMobile={isMobile} />
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  )
}
